// IM-XPR-005 (IM-UX-119, IM-UI-102) — Mapeamento oferta × perfil de cliente DENTRO do objetivo "Rever minhas ofertas".
// Cada oferta: descrição, segmentos, sugestão do InteliMove (motivo, confiança e dados usados), confirmar a sugestão,
// ajustar (vários ICPs e segmentos), marcar como transversal/institucional ou "ainda sem público definido". Salvar avança
// para a próxima oferta pendente. A tela de ICPs continua existindo como manutenção avançada. Nada é gravado sem decisão.
import { useEffect, useMemo, useRef, useState } from 'react';
import { AUDIENCE_SCOPE_LABEL, OFFERING_TREATMENT_LABEL, type OfferingMapDto, type OfferingMapItemDto } from '@intelimove/contracts';
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { contextAdapter } from '../data/context';
import { useResource } from '../hooks/useResource';
import { useCanManage } from '../pages/Targets';
import { plural } from './data';
import { ContextLink } from './returnTo';

type Filter = 'pendentes' | 'tratadas' | 'todas';
const CONF: Record<string, string> = { ALTA: 'confiança alta', MEDIA: 'confiança média', BAIXA: 'confiança baixa' };
const TONE: Record<string, string> = { RELACIONADA: 'mint', TRANSVERSAL: 'petrol', SEM_PUBLICO: 'amber', PENDENTE: 'gray' };

export function OfferMapping({ onChanged, onDone, back }: { onChanged: () => void; onDone: () => void; back: { to: string; label: string } }) {
  const { state, reload } = useResource((s) => contextAdapter.offeringMap(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(d) => <MappingBody initial={d} onChanged={onChanged} onDone={onDone} back={back} />}
    </AsyncView>
  );
}

function MappingBody({ initial, onChanged, onDone, back }: { initial: OfferingMapDto; onChanged: () => void; onDone: () => void; back: { to: string; label: string } }) {
  const [d, setD] = useState(initial);
  const [filter, setFilter] = useState<Filter>(initial.progress.pending ? 'pendentes' : 'todas');
  const [open, setOpen] = useState<string | null>(initial.nextId);
  const canManage = useCanManage();
  const p = d.progress;
  const list = d.offerings.filter((o) => (filter === 'todas' ? true : filter === 'pendentes' ? o.treatment === 'PENDENTE' : o.treatment !== 'PENDENTE'));
  const saved = (next: OfferingMapDto, id: string) => {
    setD(next);
    onChanged();
    // Avança para a próxima pendente (depois da atual, na ordem do catálogo).
    const idx = next.offerings.findIndex((o) => o.id === id);
    const after = [...next.offerings.slice(idx + 1), ...next.offerings.slice(0, idx)].find((o) => o.treatment === 'PENDENTE');
    setOpen(after?.id ?? null);
    if (!next.progress.pending && filter === 'pendentes') setFilter('todas');
  };
  if (!d.offerings.length) return <p className="xp-quiet">Nenhuma oferta confirmada ainda. Confirme suas ofertas em <ContextLink className="link-btn" to="/configuracoes/contexto/produtos" back={back}>Produtos e serviços</ContextLink>.</p>;
  if (!d.icps.length) return <p className="xp-quiet">Para relacionar as ofertas, confirme ao menos um perfil de cliente (ICP) em <ContextLink className="link-btn" to="/configuracoes/contexto/icps" back={back}>Perfis de cliente</ContextLink>.</p>;
  return (
    <div className="om">
      <p className="om-lead">Relacione cada oferta aos clientes para os quais ela faz sentido.</p>
      <dl className="om-stats" aria-label="Situação do mapeamento">
        <div><dt>Ofertas encontradas</dt><dd>{p.total}</dd></div>
        <div><dt>Relacionadas</dt><dd>{p.related}</dd></div>
        <div><dt>Transversais</dt><dd>{p.transversal}</dd></div>
        <div><dt>Sem público definido</dt><dd>{p.noAudience}</dd></div>
        <div><dt>Aguardando revisão</dt><dd>{p.pending}</dd></div>
      </dl>
      <p className="om-progress" role="status" aria-live="polite"><b>{p.treated} de {p.total} ofertas revisadas</b></p>
      {p.done && (
        <div className="om-done" role="status">
          <b>Mapeamento concluído: {p.treated} de {p.total} ofertas revisadas.</b>
          {p.noAudience > 0 && <span className="cell-sub"> {plural(p.noAudience, 'oferta ficou', 'ofertas ficaram')} sem público definido para tratar depois.</span>}
          <button type="button" className="xp-primary petrol" onClick={onDone}>Continuar para identificar lacunas e sobreposições</button>
        </div>
      )}
      <div className="filters" role="group" aria-label="Filtrar ofertas">
        {([['pendentes', `Aguardando revisão (${p.pending})`], ['tratadas', `Revisadas (${p.treated})`], ['todas', `Todas (${p.total})`]] as Array<[Filter, string]>).map(([k, l]) =>
          <button key={k} type="button" className="chip" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>)}
      </div>
      {list.length === 0 && <p className="xp-quiet">Nenhuma oferta nesta situação.</p>}
      <ul className="om-list" role="list">
        {list.map((o) => (
          <li key={o.id} className={`om-item${open === o.id ? ' open' : ''}`}>
            {open === o.id
              ? <OfferingEditor o={o} d={d} canManage={canManage} onSaved={(x) => saved(x, o.id)} onClose={() => setOpen(null)} />
              : (
                <div className="om-row">
                  <div><b>{o.name}</b><span className="cell-sub">{rowSummary(o, d)}</span></div>
                  <span className={`tag ${TONE[o.treatment]}`}>{OFFERING_TREATMENT_LABEL[o.treatment]}</span>
                  <button type="button" className="small-action" onClick={() => setOpen(o.id)} aria-label={`${o.treatment === 'PENDENTE' ? 'Revisar' : 'Ajustar'}: ${o.name}`}>{o.treatment === 'PENDENTE' ? 'Revisar' : 'Ajustar'}</button>
                </div>
              )}
          </li>
        ))}
      </ul>
      <p className="cell-sub">Manutenção avançada dos perfis: <ContextLink className="link-btn" to="/configuracoes/contexto/icps" back={back}>Perfis de cliente (ICPs)</ContextLink>.</p>
    </div>
  );
}

const icpName = (d: OfferingMapDto, id: string) => d.icps.find((i) => i.id === id)?.name ?? 'perfil';
function rowSummary(o: OfferingMapItemDto, d: OfferingMapDto): string {
  if (o.treatment === 'RELACIONADA') return o.icpIds.map((id) => icpName(d, id)).join(' · ');
  if (o.treatment === 'PENDENTE') return o.suggestion.icps.length ? `Sugestão: ${o.suggestion.icps.map((s) => s.icpName).join(' · ')}` : 'Sem sugestão automática';
  return o.audienceNote ?? AUDIENCE_SCOPE_LABEL[o.audienceScope!];
}

function OfferingEditor({ o, d, canManage, onSaved, onClose }: { o: OfferingMapItemDto; d: OfferingMapDto; canManage: boolean; onSaved: (x: OfferingMapDto) => void; onClose: () => void }) {
  const toast = useToast();
  const s = o.suggestion;
  const [mode, setMode] = useState<'view' | 'adjust' | 'none'>(o.treatment === 'PENDENTE' && !s.icps.length ? 'adjust' : 'view');
  const [icps, setIcps] = useState<string[]>(o.icpIds.length ? o.icpIds : s.icps.map((x) => x.icpId));
  const [segments, setSegments] = useState<string[]>(o.segments.length ? o.segments : s.segments);
  const [note, setNote] = useState(o.audienceNote ?? '');
  const [busy, setBusy] = useState(false);
  const head = useRef<HTMLHeadingElement>(null);
  useEffect(() => { head.current?.focus(); }, [o.id]);
  const allSegments = useMemo(() => [...new Set([...d.icps.filter((i) => icps.includes(i.id)).flatMap((i) => i.segments), ...segments])], [d.icps, icps, segments]);
  const save = async (scope: 'ICP' | 'TRANSVERSAL' | 'SEM_PUBLICO', body: { icpIds?: string[]; segments?: string[]; note?: string | null; acceptedSuggestion?: boolean }, ok: string) => {
    setBusy(true);
    try { const x = await contextAdapter.setAudience(o.id, { scope, icpIds: body.icpIds ?? [], segments: body.segments ?? [], note: body.note ?? null, acceptedSuggestion: !!body.acceptedSuggestion }); toast(ok); onSaved(x); }
    catch (e) { toast((e as Error).message, 'error'); setBusy(false); }
  };
  return (
    <div className="om-editor" aria-labelledby={`om-${o.id}`}>
      <div className="om-editor-head">
        <h3 id={`om-${o.id}`} ref={head} tabIndex={-1}>{o.name}</h3>
        <button type="button" className="link-btn" onClick={onClose}>Recolher</button>
      </div>
      <p className="cell-sub wide">{o.description || 'Sem descrição cadastrada.'}</p>
      <p className="cell-sub">Segmentos atuais: {o.segments.length ? o.segments.join(', ') : 'nenhum'} · Situação: {OFFERING_TREATMENT_LABEL[o.treatment]}</p>
      {mode === 'view' && (
        <div className="om-suggestion">
          <span className="xp-kicker">Sugestão do InteliMove</span>
          {s.icps.length ? (
            <>
              <ul className="plain-list">{s.icps.map((x) => <li key={x.icpId}><b>{x.icpName}</b> <span className="tag gray">{CONF[x.confidence]}</span><span className="cell-sub wide">{x.reason}</span></li>)}</ul>
              {s.segments.length > 0 && <p className="cell-sub">Segmentos: {s.segments.join(', ')}</p>}
              {s.transversal && <p className="cell-sub">Combina com quase todos os perfis por igual: talvez seja uma oferta transversal.</p>}
            </>
          ) : <p className="xp-quiet">{s.none}</p>}
          <details className="xp-why"><summary>Dados usados na sugestão</summary><p className="cell-sub wide">{s.basedOn.join(', ')}. A sugestão compara termos e conceitos; nada é gravado até você confirmar.</p></details>
          {canManage && (
            <div className="row-actions">
              {s.icps.length > 0 && <button type="button" className="cta" disabled={busy} onClick={() => void save('ICP', { icpIds: s.icps.map((x) => x.icpId), segments: s.segments, acceptedSuggestion: true }, 'Sugestão confirmada.')}>Confirmar sugestão</button>}
              <button type="button" className="secondary" disabled={busy} onClick={() => setMode('adjust')}>Ajustar</button>
              <button type="button" className="secondary" disabled={busy} onClick={() => setMode('none')}>Outras opções</button>
            </div>
          )}
        </div>
      )}
      {mode === 'adjust' && canManage && (
        <form className="om-adjust" onSubmit={(e) => { e.preventDefault(); void save('ICP', { icpIds: icps, segments: allSegments.filter((x) => segments.includes(x)) }, 'Oferta relacionada aos perfis escolhidos.'); }}>
          <fieldset><legend>Perfis de cliente (escolha um ou mais)</legend>
            {d.icps.map((i) => (
              <label key={i.id} className="om-check"><input type="checkbox" checked={icps.includes(i.id)} onChange={(e) => setIcps((xs) => (e.target.checked ? [...xs, i.id] : xs.filter((x) => x !== i.id)))} />
                <span><b>{i.name}</b>{i.segments.length ? <span className="cell-sub"> · {i.segments.join(', ')}</span> : null}</span></label>
            ))}
          </fieldset>
          {allSegments.length > 0 && <fieldset><legend>Segmentos</legend>
            {allSegments.map((x) => <label key={x} className="om-check inline"><input type="checkbox" checked={segments.includes(x)} onChange={(e) => setSegments((xs) => (e.target.checked ? [...xs, x] : xs.filter((y) => y !== x)))} /> {x}</label>)}
          </fieldset>}
          <div className="row-actions">
            <button className="cta" disabled={busy || !icps.length}>{busy ? 'Salvando…' : 'Salvar e avançar'}</button>
            <button type="button" className="secondary" onClick={() => setMode(s.icps.length ? 'view' : 'none')}>Voltar</button>
            {!icps.length && <span className="cell-sub">Escolha ao menos um perfil, ou use “Outras opções”.</span>}
          </div>
        </form>
      )}
      {mode === 'none' && canManage && (
        <div className="om-adjust">
          <div className="field"><label htmlFor={`om-note-${o.id}`}>Observação (opcional)</label><input id={`om-note-${o.id}`} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: oferta institucional, vale para todos os clientes" /></div>
          <div className="row-actions">
            <button type="button" className="secondary" disabled={busy} onClick={() => void save('TRANSVERSAL', { note }, 'Oferta marcada como transversal ou institucional.')}>Marcar como transversal ou institucional</button>
            <button type="button" className="secondary" disabled={busy} onClick={() => void save('SEM_PUBLICO', { note }, 'Oferta marcada como sem público definido. Ela fica listada para tratar depois.')}>Ainda não tem público definido</button>
            <button type="button" className="link-btn" onClick={() => setMode(s.icps.length ? 'view' : 'adjust')}>Voltar</button>
          </div>
          <p className="cell-sub wide">Transversal: serve a todos os perfis (ex.: consultoria geral). Sem público definido: conta como revisada, mas continua listada para tratamento posterior.</p>
        </div>
      )}
      {!canManage && <p className="cell-sub">Um administrador da organização pode relacionar as ofertas.</p>}
    </div>
  );
}
